import type { LoaderFunctionArgs, MetaFunction } from "react-router";
import { useLoaderData } from "react-router";
import { SITE_NAME, API_BASE } from "@/lib/constants";
import { safeFetchJson } from "@/lib/fetch-json";
import type { GitHubResult } from "@/lib/api";
import GitHubPage from "@/pages/GitHubPage";

export async function loader({ params }: LoaderFunctionArgs) {
  const username = params.username!;
  const user = await safeFetchJson<GitHubResult>(
    `${API_BASE}/github/${encodeURIComponent(username)}`,
    { timeoutMs: 3000 },
  );
  return { username, user };
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const username = data?.username ?? "";
  const user = data?.user;
  const origin = "https://loadlogo.com";

  if (user && !user.error) {
    const displayName = user.name || username;
    const description = `GitHub avatar for ${displayName} (@${user.username}).${user.bio ? ` ${user.bio.slice(0, 100)}` : ""} Fetch GitHub avatars via ${SITE_NAME}.`;
    return [
      { title: `${displayName} GitHub Avatar | ${SITE_NAME}` },
      { name: "description", content: description },
      { property: "og:title", content: `${displayName} GitHub Avatar | ${SITE_NAME}` },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:image", content: `${origin}/og/github/${username}` },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:url", content: `${origin}/github/${username}` },
      { property: "og:site_name", content: SITE_NAME },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: `${displayName} GitHub Avatar | ${SITE_NAME}` },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: `${origin}/og/github/${username}` },
      { tagName: "link", rel: "canonical", href: `${origin}/github/${username}` },
    ];
  }

  return [
    { title: `${username} GitHub Avatar | ${SITE_NAME}` },
    {
      name: "description",
      content: `GitHub avatar for ${username}. Fetch GitHub avatars via the ${SITE_NAME} API.`,
    },
    { property: "og:title", content: `${username} GitHub Avatar | ${SITE_NAME}` },
    {
      property: "og:description",
      content: `GitHub avatar for ${username}. Fetch GitHub avatars via the ${SITE_NAME} API.`,
    },
    { property: "og:type", content: "website" },
    { property: "og:image", content: `${origin}/og/github/${username}` },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:url", content: `${origin}/github/${username}` },
    { property: "og:site_name", content: SITE_NAME },
    { name: "twitter:card", content: "summary_large_image" },
    { tagName: "link", rel: "canonical", href: `${origin}/github/${username}` },
  ];
};

export default function GitHubRoute() {
  const { user } = useLoaderData<typeof loader>();
  return <GitHubPage serverData={user} />;
}
